import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "./SectionHeading";
import { footwearCategoryIds } from "@/mocks/categories";
import { useCategoryList, useSiteText, isVisibleCategory } from "@/lib/siteContent";
import type { Category } from "@/types";

const ACCESSORY_IDS = ["bolsas", "cintos", "semijoias"];

function CategoryCard({ category }: { category: Category }) {
  return (
    <article className="group relative overflow-hidden rounded-lg border border-background-200 bg-background-100">
      <div className="relative h-[340px] w-full overflow-hidden md:h-[400px]">
        <img
          src={category.image}
          alt={category.alt ?? `Categoria ${category.name} — calçados e acessórios Auréa`}
          title={`${category.name} — Auréa calçados femininos e acessórios`}
          loading="lazy"
          className="h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <h3 className="font-heading text-2xl text-background-50">{category.name}</h3>
          <p className="mt-1 text-xs leading-relaxed text-background-200">{category.tagline}</p>
          <Link
            to={`/categoria/${category.id}`}
            className="mt-4 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-background-50/95 px-4 py-2 text-xs font-medium text-foreground-900 transition-colors hover:bg-accent-100"
          >
            Ver
            <i className="ri-arrow-right-line text-sm" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Categories() {
  const categories = useCategoryList().filter(isVisibleCategory);
  const eyebrow = useSiteText("categories.eyebrow", "Categorias em destaque");
  const title = useSiteText("categories.title", "Encontre o par e os detalhes para o seu estilo");
  const text = useSiteText(
    "categories.text",
    "Comece pelos calçados — sandálias, scarpins, sapatilhas, mules, mocassins, tênis, rasteiras, papetes e botas. Depois complete com bolsas, cintos e semijoias.",
  );

  const byId = (id: string) => categories.find((item) => item.id === id);
  const footwear = footwearCategoryIds
    .map(byId)
    .filter((item): item is Category => Boolean(item));
  const accessories = ACCESSORY_IDS.map(byId).filter((item): item is Category => Boolean(item));

  return (
    <section id="categorias" className="w-full scroll-mt-24 bg-background-50" aria-label="Categorias em destaque">
      <div className="px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow={eyebrow} title={title} text={text} />

          <div className="mt-12 flex items-center gap-4">
            <h3 className="whitespace-nowrap font-heading text-xl text-foreground-900 md:text-2xl">
              Calçados
            </h3>
            <span className="h-px w-full bg-background-200" />
            <span className="whitespace-nowrap text-xs uppercase tracking-[0.18em] text-accent-700">
              {footwear.length} categorias
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {footwear.map((category, index) => (
              <Reveal key={category.id} delay={(index % 3) * 80}>
                <CategoryCard category={category} />
              </Reveal>
            ))}
          </div>

          <div className="mt-14 flex items-center gap-4">
            <h3 className="whitespace-nowrap font-heading text-xl text-foreground-900 md:text-2xl">
              Bolsas e acessórios
            </h3>
            <span className="h-px w-full bg-background-200" />
            <span className="whitespace-nowrap text-xs uppercase tracking-[0.18em] text-accent-700">
              Para completar o look
            </span>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {accessories.map((category, index) => (
              <Reveal key={category.id} delay={(index % 3) * 80}>
                <CategoryCard category={category} />
              </Reveal>
            ))}
          </div>

          <div className="mt-12 flex justify-center">
            <Link
              to="/novidades"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-7 py-3.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
            >
              Ver todas as novidades
              <i className="ri-arrow-right-line text-base" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}